import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { IngestedItem, Module } from "@bios/types";
import { Card, DataTable, Pill, type Column } from "@bios/ui";
import { useApiAuthed, useApiDev } from "../auth.js";

interface PageProps {
  devModeNoAuth?: boolean;
}

export function Items({ devModeNoAuth }: PageProps) {
  const { module = "media" } = useParams<{ module: Module }>();
  const m = module as Module;
  const api = devModeNoAuth ? useApiDev() : useApiAuthed();
  const [items, setItems] = useState<IngestedItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .items(m, 100)
      .then((r) => setItems(r.items))
      .catch((e) => setError(String(e)));
  }, [api, m]);

  const columns: Column<IngestedItem>[] = [
    {
      key: "title",
      header: "Title",
      render: (i) => (
        <Link to={`/app/${m}/items/${i.id}`} style={{ display: "block" }}>
          <div style={{ fontWeight: 500 }}>{i.title}</div>
          <div style={{ fontSize: 11, color: "var(--bios-text-faint)" }}>{new URL(i.url).hostname}</div>
        </Link>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (i) => (
        <Pill variant={i.status === "scored" ? "signal" : i.status === "rejected" ? "noise" : "default"}>{i.status}</Pill>
      ),
    },
    {
      key: "published",
      header: "Published",
      render: (i) => (
        <span style={{ fontSize: 12, color: "var(--bios-text-muted)" }}>
          {i.published_at ? new Date(i.published_at).toLocaleString() : "—"}
        </span>
      ),
    },
    {
      key: "ingested",
      header: "Ingested",
      render: (i) => (
        <span style={{ fontSize: 12, color: "var(--bios-text-muted)" }}>{new Date(i.ingested_at).toLocaleString()}</span>
      ),
    },
  ];

  return (
    <>
      <div className="bios-page-header">
        <div>
          <h1 className="bios-page-title">Ingested items</h1>
          <p className="bios-page-sub">Filtered, deduplicated, classified, scored.</p>
        </div>
      </div>
      {error && (
        <Card>
          <p style={{ color: "var(--bios-noise)", fontSize: 13 }}>{error}</p>
        </Card>
      )}
      <div className="bios-table-only">
        <Card>
          <DataTable columns={columns} rows={items} rowKey={(i) => i.id} empty="No items ingested yet." />
        </Card>
      </div>
      <div className="bios-mobile-only" style={{ display: "grid", gap: 12 }}>
        {items.length === 0 ? (
          <Card>
            <p className="bios-empty">No items ingested yet.</p>
          </Card>
        ) : (
          items.map((i) => {
            let host = "";
            try {
              host = new URL(i.url).hostname;
            } catch {
              host = "";
            }
            return (
              <Card key={i.id}>
                <Link
                  to={`/app/${m}/items/${i.id}`}
                  className="bios-item-card"
                  style={{ display: "block", textDecoration: "none", color: "inherit" }}
                >
                  <div className="bios-item-card-head">
                    <div className="bios-item-card-title">{i.title}</div>
                    <Pill
                      variant={
                        i.status === "scored"
                          ? "signal"
                          : i.status === "rejected"
                          ? "noise"
                          : "default"
                      }
                    >
                      {i.status}
                    </Pill>
                  </div>
                  {host && <div className="bios-item-card-host">{host}</div>}
                  <div className="bios-item-card-meta">
                    {i.published_at && (
                      <span>Published: {new Date(i.published_at).toLocaleString()}</span>
                    )}
                    <span>Ingested: {new Date(i.ingested_at).toLocaleString()}</span>
                  </div>
                </Link>
              </Card>
            );
          })
        )}
      </div>
    </>
  );
}
